import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { ApiError } from '@/shared/api/http';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';

import { useConfirmEmailVerification } from '../api/auth';
import { tokenFromQuery } from '../model/verification';

/* Redeeming a verification link. ADR-014.
 *
 * **Five states, and they are five because collapsing any two sends somebody to the wrong place.**
 *
 * * *No token in the URL* — the link was mangled on the way here, truncated by a mail client or
 *   copied without its query string. Deliberately **not** the same message as a rejected token:
 *   that one means the platform read a token and refused it, which is nearly always expiry, and
 *   telling this reader their link expired sends them to ask for another that will arrive equally
 *   broken. `ResetPasswordForm` keeps the same two apart and is tested for it.
 * * *Working* — a spinner-shaped message rather than a blank card, because this panel does its work
 *   on mount and a reader who sees nothing assumes the link is dead.
 * * *Verified* — the first redemption.
 * * *Already verified* — a second click, a prefetching mail client, or a restored tab. Reported as
 *   success rather than an error: the account works, and an error here is a support ticket about
 *   nothing.
 * * *Refused* — the server's own sentence, which is one identical message for an unknown, expired,
 *   superseded or since-deleted link. The screen does not try to improve on it, because
 *   distinguishing those is exactly what the server declines to do.
 *
 * **It redeems on mount rather than behind a button**, which is the opposite of the platform's usual
 * posture and is right here: the person already expressed intent by clicking a link in their mail,
 * and a second confirmation would be a button whose only purpose is to make them click twice. The
 * `ref` guard is what stops React 18's development double-mount spending the token twice — harmless
 * against an idempotent route, and it would still show "already verified" to somebody who had done
 * nothing wrong.
 */
const REDIRECT_SECONDS = 3;
/** How long the success card stays before it sends the reader to sign in.
 *
 * Long enough to read "Your email is verified" and short enough that nobody wonders whether the
 * page is stuck. Three rather than a longer pause because the card carries a button: the timer is
 * a convenience for somebody who has stopped reading, not the primary way out.
 */

export function VerifyEmailPanel({
  onVerified,
}: {
  readonly onVerified: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { search } = useLocation();
  const parsed = tokenFromQuery(search);
  /* **No mount effect, no ref guard, and no local copy of the result.** All three are gone, and
     their absence is the fix rather than a tidy-up.

     The shape here was a `useMutation` fired from a mount effect behind a `useRef`, and it could
     not survive `StrictMode` — which `main.tsx` wraps the whole app in. React double-invokes every
     effect in development: the first run set the ref and sent the request, React then simulated an
     unmount, and the second run found the ref already true and sent nothing. In between the
     mutation observer was unsubscribed and resubscribed, so it came back **idle**, and a per-call
     `onSuccess` tied to the old observer never fired. The request answered 200 and the component
     never learnt it — the screen sat on "Verifying your email" for ever, which is precisely what
     was reported.

     Mirroring the result into `useState` from `onSuccess` was an attempt at the same fix and has
     the same hole: when that callback does not fire, the mirror stays null and `isIdle` keeps the
     working card up. Both mechanisms depended on the observer surviving, and it does not.

     A query has no such gap: the answer lives in the `QueryClient` cache under
     `['email-verification', token]`, so a resubscribing observer reads the cached result instead of
     starting from nothing. See `useConfirmEmailVerification` for why a read is also the honest
     model for this. */
  const confirm = useConfirmEmailVerification(
    parsed.kind === 'present' ? parsed.token : null,
  );
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  /* **The redirect is a countdown rather than a bare `setTimeout`, and the number is on the
   * screen.** A page that navigates away on its own with no warning reads as a crash — the reader
   * did not ask to leave and cannot tell whether the verification succeeded or the app fell over.
   * Showing the seconds makes it an announcement instead, and the button beside it means nobody has
   * to wait for it.
   *
   * Guarded on `isSuccess`, so a refused link never starts a timer that would take the reader off
   * the one screen explaining why it was refused.
   *
   * The interval is cleared on unmount, which matters because the last tick navigates: without it a
   * reader who clicks the button at 1 second gets `onVerified` twice, and React logs a state update
   * on an unmounted component. */
  const done = useRef(onVerified);
  done.current = onVerified;

  useEffect(() => {
    if (!confirm.isSuccess) return undefined;
    if (secondsLeft <= 0) {
      /* Called through a ref so `onVerified` is **not** a dependency below. `VerifyEmailScreen`
         passes an inline arrow, which is a new function on every render — as a dependency it would
         clear and restart the 1000ms timer on each one, so a parent re-rendering faster than once a
         second would freeze the countdown at 3 and never navigate. The ref keeps the latest
         callback without making its identity part of when the timer runs. */
      done.current();
      return undefined;
    }
    const tick = setTimeout(() => {
      setSecondsLeft((remaining) => remaining - 1);
    }, 1000);
    return () => {
      clearTimeout(tick);
    };
  }, [confirm.isSuccess, secondsLeft]);

  if (parsed.kind === 'missing') {
    return (
      <>
        <h1 className="lq-auth__heading">{t('auth.verify.title')}</h1>
        <Alert tone="error" title={t('auth.verify.missing.title')}>
          {t('auth.verify.missing.body')}
        </Alert>
      </>
    );
  }

  if (confirm.isPending) {
    return (
      <>
        <h1 className="lq-auth__heading">{t('auth.verify.title')}</h1>
        <Alert tone="notice" title={t('auth.verify.working.title')}>
          {/* **This body was `t('  ')`** — a lookup for a key of two spaces, which i18next returns
              verbatim, so the card rendered a title over an empty line. Harmless-looking and the
              worst place for it: this is the one state a reader sees while nothing is happening
              yet, and a card with no explanation reads as a dead link rather than as work in
              progress. */}
          {t('auth.verify.working.body')}
        </Alert>
      </>
    );
  }

  if (confirm.isError) {
    /* A 422 is the expected refusal — expired, spent, unknown — so it is a notice rather than an
       error; anything else is genuinely wrong with the request or the platform. The server's
       sentence is rendered as-is: it is one identical message for four facts, on purpose, and a
       screen that tried to be more specific would undo that. */
    const expected = confirm.error instanceof ApiError && confirm.error.status === 422;
    return (
      <>
        <h1 className="lq-auth__heading">{t('auth.verify.title')}</h1>
        <Alert
          tone={expected ? 'notice' : 'error'}
          title={t('auth.verify.refused.title')}
        >
          {confirm.error.message}
        </Alert>
      </>
    );
  }

  /* Off the query's own data, so there is one source for "was this a repeat click". A separate
     copy in component state was the thing that could disagree with the response. */
  const already = confirm.data.already_verified;
  return (
    <>
      <h1 className="lq-auth__heading">{t('auth.verify.title')}</h1>
      <Alert
        tone="ok"
        title={t(already ? 'auth.verify.already.title' : 'auth.verify.done.title')}
      >
        {t(already ? 'auth.verify.already.body' : 'auth.verify.done.body')}
      </Alert>
      <div className="lq-auth__act">
        <Button
          tone="primary"
          onClick={() => {
            onVerified();
          }}
        >
          {t('auth.verify.toSignIn')}
        </Button>
      </div>
      <p className="lq-dim lq-auth__aside">
        {t('auth.verify.redirecting', { count: Math.max(secondsLeft, 0) })}
      </p>
    </>
  );
}
